/**
 * المرحلة 1.35 — named discounts as the screens apply them: the same rules
 * as Backend/lib/priceDiscounts.js, for the quick card's preview and the
 * editor. A preview, not the price: the server applies them again from its
 * own rows, and a discount in the request is an id and a reason, never an
 * amount. Tests/suites/frontend/p135-center.test.mjs keeps the two equal.
 *
 * `applyDiscounts(..., reasons: false)` previews the amounts while the desk
 * is still typing the reasons; the card's blockers ask for them
 * (domain/voucher.js missing).
 */

export const KINDS = Object.freeze(['percent', 'amount']);
export const TARGETS = Object.freeze(['services', 'invoice']);
const MAX_AMOUNT = 10000000;
export const MAX_DISCOUNTS = 100;
export const MIN_REASON = 3;

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const text = (v, max) => (v === undefined || v === null ? null : String(v).trim().slice(0, max) || null);
const digits = (v) => String(v).replace(/[٠-٩]/g, (d) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)));
const plain = (row) => (row && typeof row.get === 'function' ? row.get({ plain: true }) : row);

/**
 * Normalise what an editor sent. Only the fields given are returned, so an
 * edit that renames a discount leaves its value alone.
 *
 * @returns {{ error: string } | { value: object }}
 */
export function cleanDiscount(body = {}, { creating = false, current = null } = {}) {
  const value = {};
  if (creating || body.name !== undefined) {
    const name = text(body.name, 80);
    if (!name) return { error: 'اكتب اسم الخصم' };
    value.name = name;
  }
  if (creating || body.kind !== undefined) value.kind = KINDS.includes(body.kind) ? body.kind : 'percent';
  if (creating || body.target !== undefined) value.target = TARGETS.includes(body.target) ? body.target : 'services';
  const kind = value.kind || (current && current.kind) || 'percent';
  if (creating || body.value !== undefined || body.kind !== undefined) {
    const raw = body.value === undefined ? current && current.value : body.value;
    const n = Number(digits(raw === null || raw === undefined ? '' : raw));
    if (raw === '' || raw === null || raw === undefined || !Number.isFinite(n) || n <= 0) return { error: kind === 'percent' ? 'اكتب نسبة الخصم' : 'اكتب مبلغ الخصم' };
    if (kind === 'percent' && n > 100) return { error: 'النسبة من 1 لـ 100' };
    if (n > MAX_AMOUNT) return { error: 'مبلغ الخصم غير صحيح' };
    value.value = round2(n);
  }
  if (body.itemIds !== undefined) {
    const ids = (Array.isArray(body.itemIds) ? body.itemIds : []).map((id) => parseInt(id, 10)).filter((id) => Number.isFinite(id) && id > 0);
    value.itemIds = [...new Set(ids)].slice(0, 200);
  } else if (creating) value.itemIds = [];
  if (body.note !== undefined) value.note = text(body.note, 300);
  if (body.active !== undefined) value.active = body.active === true || body.active === 'true';
  if (body.sortOrder !== undefined) {
    const n = parseInt(body.sortOrder, 10);
    value.sortOrder = Number.isFinite(n) ? Math.max(0, Math.min(100000, n)) : 0;
  }
  return { value };
}

/** A discount as the desk and the editor read it. */
export function present(row) {
  const d = plain(row) || {};
  return {
    id: Number(d.id),
    name: d.name,
    kind: KINDS.includes(d.kind) ? d.kind : 'percent',
    value: round2(d.value),
    target: TARGETS.includes(d.target) ? d.target : 'services',
    itemIds: (Array.isArray(d.itemIds) ? d.itemIds : []).map(Number),
    note: d.note || null,
    active: d.active !== false,
    sortOrder: Number(d.sortOrder) || 0,
  };
}

/**
 * Does a services discount reach this price list leaf? Every service when it
 * names none; else the leaf, or a category or service above it, is named.
 *
 * @param {object} discount  present()
 * @param {{id:number, pathIds?:number[]}} leaf  lib/priceList.leaves
 */
export function reaches(discount, leaf) {
  if (!discount || discount.target !== 'services' || !leaf) return false;
  const ids = Array.isArray(discount.itemIds) ? discount.itemIds.map(Number) : [];
  if (!ids.length) return true;
  const path = Array.isArray(leaf.pathIds) && leaf.pathIds.length ? leaf.pathIds : [leaf.id];
  return path.some((id) => ids.includes(Number(id)));
}

/** What the discount takes off a base: the percentage, or the amount, never more than the base. */
export function amountOff(discount, base) {
  const b = Math.max(0, round2(base));
  if (!discount || b <= 0) return 0;
  const off = discount.kind === 'amount' ? Number(discount.value) : (b * Number(discount.value)) / 100;
  return round2(Math.min(b, Math.max(0, off)));
}

/** How the discount reads on a voucher: «خصم الأقارب 20%» / «خصم الأقارب 200». */
export function labelOf(discount) {
  if (!discount) return '';
  const v = round2(discount.value);
  return discount.kind === 'percent' ? `${discount.name} ${v}%` : `${discount.name} ${v}`;
}

/** The snapshot a voucher keeps: what was applied, how much, and why. */
const snapshot = (discount, amount, reason) => ({
  id: Number(discount.id),
  name: discount.name,
  kind: discount.kind,
  value: round2(discount.value),
  amount: round2(amount),
  reason,
});

/**
 * Apply the desk's discounts to lines already priced from the list.
 *
 * @param {object} input
 * @param {object[]} input.lines      lib/priceList.priceLines(...).lines, in order
 * @param {object[]} input.asked      the request's lines, same order: { discountId, discountReason }
 * @param {object|null} input.invoice the request's total discount: { discountId, reason }
 * @param {object[]} input.discounts  the institution's discounts (present() or rows)
 * @param {object[]} input.leaves     lib/priceList.leaves, to know a line's category
 * @param {boolean} [input.reasons]  false for the card's preview: the amounts before the reasons are typed
 * @returns {{ error, code } | { lines, subtotal, lineDiscounts, invoice, discountTotal, total }}
 */
export function applyDiscounts({ lines = [], asked = [], invoice = null, discounts = [], leaves = [], reasons = true }) {
  const byId = new Map(discounts.map(present).filter((d) => d.active).map((d) => [d.id, d]));
  const leafOf = new Map(leaves.map((l) => [Number(l.id), l]));
  const out = [];
  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];
    const gross = round2(line.amount);
    const want = asked[i] || {};
    const id = parseInt(want.discountId, 10);
    if (!Number.isFinite(id)) {
      out.push({ ...line, amount: gross });
      continue;
    }
    const discount = byId.get(id);
    if (!discount) return { error: 'الخصم ده مش موجود أو اتقفل', code: 'DISCOUNT' };
    const leaf = leafOf.get(Number(line.priceItemId)) || { id: Number(line.priceItemId) };
    if (!reaches(discount, leaf)) return { error: `«${discount.name}» مش على «${line.label}»`, code: 'DISCOUNT' };
    const reason = text(want.discountReason, 300);
    if (reasons && (!reason || reason.length < MIN_REASON)) return { error: `اكتب سبب «${discount.name}» على «${line.label}»`, code: 'DISCOUNT_REASON' };
    const off = amountOff(discount, gross);
    out.push({ ...line, gross, discount: snapshot(discount, off, reason), amount: round2(gross - off) });
  }
  const subtotal = round2(out.reduce((s, l) => s + (l.gross === undefined ? l.amount : l.gross), 0));
  const lineDiscounts = round2(out.reduce((s, l) => s + (l.discount ? l.discount.amount : 0), 0));
  const afterLines = round2(subtotal - lineDiscounts);

  let applied = null;
  const invoiceId = invoice ? parseInt(invoice.discountId, 10) : NaN;
  if (Number.isFinite(invoiceId)) {
    const discount = byId.get(invoiceId);
    if (!discount) return { error: 'الخصم ده مش موجود أو اتقفل', code: 'DISCOUNT' };
    if (discount.target !== 'invoice') return { error: `«${discount.name}» على الخدمات مش على إجمالي الإيصال`, code: 'DISCOUNT' };
    const reason = text(invoice.reason, 300);
    if (reasons && (!reason || reason.length < MIN_REASON)) return { error: `اكتب سبب «${discount.name}» على الإجمالي`, code: 'DISCOUNT_REASON' };
    applied = snapshot(discount, amountOff(discount, afterLines), reason);
  }
  const discountTotal = round2(lineDiscounts + (applied ? applied.amount : 0));
  return { lines: out, subtotal, lineDiscounts, invoice: applied, discountTotal, total: round2(subtotal - discountTotal) };
}

/** The receipt's `discount` column: null when nothing was taken off. */
export function summaryOf(applied) {
  if (!applied || !applied.discountTotal) return null;
  return { subtotal: applied.subtotal, lines: applied.lineDiscounts, invoice: applied.invoice, total: applied.discountTotal };
}
